<template>
  <footer
    :id="props.id"
    :class="['footer', { 'footer-blue': props.variant === 'blue' }]"
  >
    <div class="container-content footer-content">
      <div class="footer-logo">
        <div v-if="$slots.logo" class="logo-content">
          <span aria-hidden="true" class="footer-logo-powered-by"
            >powered by</span
          >
          <slot name="logo"></slot>
        </div>
      </div>
      <nav class="footer-links" aria-label="Footer links">
        <div
          v-for="(link, index) in props.links"
          :key="index"
          class="footer-link-item"
        >
          <component
            :is="!link.to ? 'button' : link.external ? 'a' : RouterLink"
            :href="
              link.external
                ? link.to
                : typeof link.to === 'string'
                  ? link.to
                  : undefined
            "
            :rel="link.external ? 'noopener noreferrer' : null"
            :target="link.external ? '_blank' : null"
            :to="!link.external && link.to ? link.to : undefined"
            :type="!link.to ? 'button' : undefined"
            :aria-label="link.label || link.ariaLabel || 'Footer link'"
            class="footer-link"
            @click="link.onClick"
          >
            {{ link.label || '' }}
          </component>
        </div>
      </nav>
    </div>
  </footer>
</template>

<script setup>
import '@/v2/design-system/styles/main.css';
import { RouterLink } from 'vue-router';

const props = defineProps({
  id: { type: String, default: 'bm-footer' },
  variant: {
    type: String,
    required: false,
    default: 'dark',
    validator: value => ['dark', 'blue'].includes(value)
  },
  links: {
    type: Array,
    required: false,
    default: () => []
  }
});
</script>

<style scoped>
.footer {
  flex-shrink: 0;
  background-color: var(--neutral-surface-darkest);
  color: var(--neutral-text-icon-negative);
  font-family: var(--body-small-reg-family), sans-serif;
  font-style: normal;
  font-size: var(--label-caption-reg-size);
  font-weight: var(--label-caption-reg-weight);
  line-height: var(--label-caption-reg-line-height);
  padding: var(--pad-space-sp-32) var(--pad-space-sp-40);
}

.footer.footer-blue {
  background-color: var(--blue-gls);
}

.footer-content {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  width: 100%;
}

.footer-links {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.footer-link-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.footer-link-item:not(:last-child)::after {
  content: '';
  display: block;
  height: 1px;
  width: 14px;
  background-color: var(--neutral-border-disabled);
  margin: var(--pad-space-sp-8) auto;
}

.footer-links .footer-link {
  color: var(--neutral-text-icon-negative);
  text-decoration: none;
  cursor: pointer;
}

button.footer-link {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
}

.footer-link:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 3px;
  border-radius: 2px;
}

.logo-content {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: var(--pad-space-sp-32);
}

.footer-logo-powered-by {
  white-space: nowrap;
  color: var(--neutral-text-icon-subtle);
}

@media (min-width: 1024px) {
  .footer {
    font-size: var(--body-small-reg-size);
    font-weight: var(--body-small-reg-weight);
    line-height: var(--line-height-lead-sm);
    padding: var(--pad-space-sp-20) 0;
  }

  .footer-content {
    flex-direction: row;
    justify-content: space-between;
    margin: 0 auto;
  }

  .footer-links {
    flex-direction: row;
  }

  .footer-link-item {
    flex-direction: row;
  }

  .footer-link-item + .footer-link-item::before {
    content: '|';
    margin: 0 var(--pad-space-sp-16);
    color: var(--neutral-text-icon-negative);
  }

  .footer-link-item:not(:last-child)::after {
    content: none;
    display: none;
  }

  .logo-content {
    margin-top: var(--pad-space-sp-0);
  }
}
</style>
